import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// components
import CheckoutForm from "core/CheckoutForm";
import DisclaimerBodyText from "subscriptions/components/DisclaimerBodyText";
import {
    CheckoutProvider,
    useUpdateBillingInfo
} from "frontend/context/CheckoutContext";
import { PayPalNounProSubProvider } from "frontend/context/PayPalNounProSubContext";
import PageMeta from "core/PageMeta";

// helpers
import { processTransaction } from "subscriptions/helpers";
import { useHistory } from "react-router-dom";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCheckoutFormGeneralError } from "frontend/context/CheckoutContext";
import { useAuth } from "frontend/hooks/useAuth";

// styles
import { FormContainer } from "subscriptions/styles";
import { GeneralFormErrorMessage } from "icons/IconCheckout/styles";

// data
import {
    NOUN_PRO_EDU_CONFIRMATION_PAGE,
    NOUN_PRO_EDU_LANDING_PAGE,
    NOUN_PRO_TEAM_CONFIRMATION_PAGE
} from "data/links";
import {
    EDU_PRO_YEARLY_PLAN,
    subscribeURL,
    PRO_YEARLY_PLAN,
    subscribeTeamURL
} from "subscriptions/data";
import {
    TRACK_ITEM_ICON,
    TRACK_EDU_CHECKOUT,
    TRACK_PURCHASE_PRO_YEAR,
    trackViewCheckout,
    trackPurchase
} from "core/trackEvent";

const UnwrappedNounProEduForm = ({
    itemPrice,
    showOrderSummary,
    setForm,
    form
}) => {
    const history = useHistory();
    const stripe = useStripe();
    const elements = useElements();
    const { currentUser } = useAuth();
    const {
        generalFormError,
        setGeneralFormError
    } = useCheckoutFormGeneralError();
    const { submitBillingInfo } = useUpdateBillingInfo();

    const [saveCard, setSaveCard] = useState(false);
    const [loading, setLoading] = useState(false);

    const trackingData = {
        itemType: TRACK_ITEM_ICON,
        userType: TRACK_EDU_CHECKOUT,
        purchaseType: TRACK_PURCHASE_PRO_YEAR,
        price: itemPrice
    };
    useEffect(() => {
        trackViewCheckout(trackingData);
    }, []);

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setGeneralFormError("");
    };

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        if (!form.name) {
            return setGeneralFormError("Please fill out all required fields");
        }

        setLoading(true);

        const isSuccess = await submitBillingInfo();
        if (isSuccess) finalPurchase();
    };

    const finalPurchase = async () => {
        const routeURL = `/${currentUser.username}${subscribeURL}`;

        const body = {
            plan: EDU_PRO_YEARLY_PLAN
        };

        const { formError, success } = await processTransaction(
            form,
            body,
            routeURL,
            stripe,
            elements
        );

        if (success) {
            trackPurchase(trackingData);
            history.replace(NOUN_PRO_EDU_CONFIRMATION_PAGE);
        } else if (formError) {
            setLoading(false);
            setGeneralFormError(formError);
        }
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    return (
        <>
            <PageMeta title="Checkout" indexContent="noindex, nofollow" />
            <FormContainer>
                {generalFormError && (
                    <GeneralFormErrorMessage
                        handleDismissClick={() => setGeneralFormError("")}
                        withHeader
                    >
                        {generalFormError}
                    </GeneralFormErrorMessage>
                )}
                <CheckoutForm
                    cancelLink={NOUN_PRO_EDU_LANDING_PAGE}
                    form={form}
                    handleFormChange={handleFormChange}
                    hasSavedCard={false}
                    hideCardLinks
                    isSubmitDisabled={false}
                    itemPrice={itemPrice}
                    loading={loading}
                    saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                    saveCardInitialValue={false}
                    showOrderSummary={showOrderSummary}
                    showPayPalPayment={false}
                    showPromoCode={false} // no promos allowed on EDU
                    submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                    submitText={"Start Subscription"}
                />
            </FormContainer>
            <DisclaimerBodyText licenseSelection={PRO_YEARLY_PLAN} />
        </>
    );
};

const NounProEduForm = props => (
    <CheckoutProvider>
        <PayPalNounProSubProvider>
            <UnwrappedNounProEduForm {...props} />
        </PayPalNounProSubProvider>
    </CheckoutProvider>
);

NounProEduForm.propTypes = {
    itemPrice: PropTypes.number.isRequired,
    showOrderSummary: PropTypes.bool.isRequired,
    setForm: PropTypes.func.isRequired,
    form: PropTypes.shape({
        name: PropTypes.string,
        address_zip: PropTypes.string,
        receipt_info: PropTypes.string
    })
};

export default NounProEduForm;
